import { useNotify } from '@globaleris/ui.library'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumTipoCicloFacturacion, EnumTipoFacturacion, EnumTipoSujeto } from '../../../api'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import {
  fetchInvoiceCycleExecution,
  updateInvoiceCycleExecution
} from '../../invoiceCycles/invoiceCycles.service'
import { RegenerateFormData } from '../settlements.models'
import { closeExecutionCycleAsync, generateLiquidationsAsync } from '../settlements.service'

interface UseExecutionCycleActionsParams {
  executionCycleId: number
  invoiceCycleId: number
  invoiceCycleDescription?: string
  invoiceCycleType?: EnumTipoCicloFacturacion
  subjectType?: EnumTipoSujeto
  billingType?: EnumTipoFacturacion
  /** Estado actual del ciclo — se pasa como originState al navegar a processing para que el polling sepa de dónde viene */
  currentCycleState?: EnumEstadoCiclo
  executionInitialData?: RegenerateFormData
}

/**
 * Custom hook que proporciona handlers compartidos para acciones del ciclo de ejecución.
 * Se utiliza en las pantallas de Settlements y SettlementsInvoices para mantener
 * la lógica consistente entre ambas.
 *
 * @param params - Parámetros necesarios para las acciones del ciclo
 * @param params.executionCycleId - ID del ciclo de ejecución
 * @param params.invoiceCycleId - ID del ciclo de facturación
 * @param params.invoiceCycleDescription - Descripción del ciclo (opcional)
 * @param params.invoiceCycleType - Tipo de ciclo de facturación -- requerido para redirección solamente
 * @param params.subjectType - Tipo de sujeto -- requerido para redirección solamente
 * @param params.billingType - Tipo de facturación -- requerido para redirección solamente
 *
 * @returns Objeto con los handlers:
 * - handleRegenerateExecutionCycle: Regenera el ciclo de ejecución
 * - handleCloseExecutionCycle: Cierra el ciclo de ejecución
 */
export const useExecutionCycleActions = ({
  executionCycleId,
  invoiceCycleId,
  invoiceCycleDescription,
  invoiceCycleType,
  subjectType,
  billingType,
  currentCycleState,
  executionInitialData
}: UseExecutionCycleActionsParams) => {
  const [t] = useTranslation(['settlementsInvoices'])
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { info, error } = useNotify()
  const history = useHistory()
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false)

  const handleRegenerateExecutionCycle = async () => {
    const isAnnulationOrRefacturation =
      billingType === EnumTipoFacturacion.Anuladora ||
      billingType === EnumTipoFacturacion.Refacturacion

    await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REGENERATE_EXECUTION_CYCLE).then(
      async () => {
        if (isAnnulationOrRefacturation) {
          const executionResponse = await fetchInvoiceCycleExecution(executionCycleId)

          const patchResponse = await updateInvoiceCycleExecution(executionCycleId, {
            cycleStatus: EnumEstadoCiclo.PdteValidarListado
          })

          if (!patchResponse.success) {
            error(t('settlementsInvoices:fetchResponse.updateExecutionCycleState.error'))
            closeConfirmation()
            return
          }

          closeConfirmation()
          history.push(NonAuthRoutes.SettlementsAnnulation, {
            executionCycleId,
            invoiceCycleId,
            billingType,
            startDate: executionResponse.data?.startDate,
            endDate: executionResponse.data?.endDate
          })
        } else {
          closeConfirmation()
          setRegenerateDialogOpen(true)
        }
      }
    )
  }

  const handleRegenerateDialogClose = async (formData?: RegenerateFormData) => {
    setRegenerateDialogOpen(false)
    if (!formData) return
    const response = await generateLiquidationsAsync(executionCycleId, formData)
    if (response.success) {
      info(t('settlementsInvoices:fetchResponse.generateLiquidations.inProgress'))
    } else {
      error(t('settlementsInvoices:fetchResponse.generateLiquidations.error'))
      return
    }
    history.push(NonAuthRoutes.invoiceCycleProcessing, {
      executionCycleId,
      invoiceCycleId,
      cycleName: invoiceCycleDescription,
      originState: currentCycleState
    })
  }

  /**
   * Cierra el ciclo de ejecución llamando al servicio correspondiente.
   * Las fechas deben ser proporcionadas desde un formulario aparte.
   *
   * @param generateInvoices - true si debe generar facturas (deleteLiquidations=false), false si no (deleteLiquidations=true)
   * @param startDate - Fecha de inicio del ciclo de ejecución (ISO string)
   * @param endDate - Fecha de fin del ciclo de ejecución (ISO string)
   * @param reInvoiceType - Tipo de re-facturación (opcional, solo para ciertos tipos de ciclo)
   * @param groupingType - Tipo de agrupación para la generación de facturas (opcional, solo para ciertos tipos de ciclo)
   */
  const closeExecutionCycle = async (
    generateInvoices: boolean,
    startDate?: string,
    endDate?: string,
    reInvoiceType?: number,
    groupingType?: number
  ) => {
    try {
      const response = await closeExecutionCycleAsync({
        executionId: executionCycleId,
        deleteLiquidations: !generateInvoices, // invertido: si genera facturas, NO elimina liquidaciones
        cfExecutionStartDate: startDate,
        cfExecutionEndDate: endDate,
        reInvoiceType,
        groupingType
      })

      if (response.success) {
        closeConfirmation()
        info(t('settlementsInvoices:fetchResponse.closeExecutionCycle.success'))
        history.push(NonAuthRoutes.invoiceCycleFinished, {
          executionCycleId,
          invoiceCycleId,
          invoiceCycleType,
          subjectType,
          billingType
        })
      } else {
        error(t('settlementsInvoices:fetchResponse.closeExecutionCycle.error'))
      }
    } catch (err) {
      error(t('settlementsInvoices:fetchResponse.closeExecutionCycle.error'))
    }
  }

  return {
    handleRegenerateExecutionCycle,
    closeExecutionCycle,
    regenerateDialogOpen,
    handleRegenerateDialogClose,
    regenerateInitialData: executionInitialData
  }
}
